import { useState, useEffect, useCallback, useRef } from 'react'
import { searchApi, articleApi, SearchResultRow, ByArticleResultRow, KeywordInput, PostSearchFilter, ArticleExtras, ByArticleSortKey, SortDir } from '../api/search'
import { datasetApi } from '../api/datasets'
import { aiApi, RegexBuildResponse } from '../api/ai'
import { useSession } from '../contexts/SessionContext'
import { useCorpus } from '../contexts/CorpusContext'
import RegexCheatsheet from '../components/RegexCheatsheet'
import BookmarkStar from '../components/BookmarkStar'

const SECTION_OPTIONS = [
  'Title', 'Abstract', 'Introduction', 'Methods', 'Results', 'Discussion',
  'Conclusion', 'Figure_Legend', 'Table_Legend', 'Supplementary'
]

const ARTICLE_TYPE_OPTIONS = ['Research', 'Review']

const WORKSPACE_KEY = 'pmc_ev_workspace'

function articleUrl(row: { pmcid: string; pmid?: string | null }): string {
  if (row.pmcid.startsWith('PMID_')) {
    // Medline-only record: the PMID lives in the synthetic key (row.pmid may be empty).
    const pmid = row.pmid || row.pmcid.replace('PMID_', '')
    return `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`
  }
  return `https://www.ncbi.nlm.nih.gov/pmc/articles/${row.pmcid}`
}

// Display label for the ID column: strip the synthetic `PMID_` key so Medline-only
// records show a clean `PMID:39066974` instead of `PMID_39066974`.
function articleLabel(row: { pmcid: string; pmid?: string | null }): string {
  if (row.pmcid.startsWith('PMID_')) {
    return `PMID:${row.pmid ?? row.pmcid.replace('PMID_', '')}`
  }
  return row.pmcid
}

function SortHeader({
  col, label, sortBy, sortDir, onSort,
}: {
  col: ByArticleSortKey
  label: string
  sortBy: ByArticleSortKey
  sortDir: SortDir
  onSort: (c: ByArticleSortKey) => void
}) {
  const active = sortBy === col
  const arrow = !active ? '↕' : sortDir === 'asc' ? '↑' : '↓'
  return (
    <th
      className="px-4 py-2 text-left font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-100"
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-xs ${active ? 'text-blue-600' : 'text-gray-300'}`}>{arrow}</span>
      </span>
    </th>
  )
}

interface WorkspaceState {
  searchId: string
  activeSessionId: string
  lastDatasetId: string
  keywords: KeywordInput[]
  yearRange: [number, number]
  selectedSections: string[]
  articleTypes: string[]
  entireManuscript: boolean
  includeNonOA: boolean
  searchedKeywords: string[]
  isFiltered: boolean
  cooccurrenceKws: string[]
  cooccurrenceLevel: 'manuscript' | 'section' | 'sentence'
  titleFilter: string
  sentenceFilter: string
  sectionFilter: string
  filterYearRange: [number, number]
  keywordMinCounts: Record<string, number>
  resultsViewTab: 'by-article' | 'detailed'
}

export default function SearchPage() {
  const [keywords, setKeywords] = useState<KeywordInput[]>([{ term: '', case_sensitive: false }])
  const [yearRange, setYearRange] = useState<[number, number]>([2000, 2026])
  const [selectedSections, setSelectedSections] = useState<string[]>([])
  const [articleTypes, setArticleTypes] = useState<string[]>(['Research', 'Review'])
  const [entireManuscript, setEntireManuscript] = useState(true)
  const [includeNonOA, setIncludeNonOA] = useState(false)

  // PMCID restriction source: upload a file, or pick a saved dataset from the
  // current session (only its PMCID column is used).
  const [pmcidListMode, setPmcidListMode] = useState<'none' | 'file' | 'saved'>('none')
  const [selectedRestrictionDataset, setSelectedRestrictionDataset] = useState('')
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  // Results & pagination state
  const [results, setResults] = useState<SearchResultRow[]>([])
  const [searchId, setSearchId] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [isLoadingPage, setIsLoadingPage] = useState(false)
  const [activeTab, setActiveTab] = useState<'search' | 'filter'>('search')

  // AI Regex Builder state
  const [showRegexBuilder, setShowRegexBuilder] = useState(false)
  const [showCheatsheet, setShowCheatsheet] = useState(false)
  const [regexDescription, setRegexDescription] = useState('')
  const [isBuildingRegex, setIsBuildingRegex] = useState(false)
  const [regexResult, setRegexResult] = useState<RegexBuildResponse | null>(null)

  // Session state — sessions + active selection come from the shared context
  // (also surfaced in the top-nav picker); only the per-session dataset list is local.
  const { activeSessionId, setActiveSessionId, refreshSessions } = useSession()
  const { activeCorpusId, activeCorpus } = useCorpus()
  const [sessionDatasets, setSessionDatasets] = useState<{ dataset_id: string; name: string; row_count: number }[]>([])

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [isFiltered, setIsFiltered] = useState(false)

  // Keywords that were actually searched (for co-occurrence checkboxes)
  const [searchedKeywords, setSearchedKeywords] = useState<string[]>([])

  // Results view: by-article (default) or detailed
  const [resultsViewTab, setResultsViewTab] = useState<'by-article' | 'detailed'>('by-article')

  // By-article results fetched from backend (for search-based data)
  const [articleResults, setArticleResults] = useState<ByArticleResultRow[]>([])

  // By-article sort state (server-side sort for paginated, client-side for aggregated)
  const [sortBy, setSortBy] = useState<ByArticleSortKey>('year')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // Row expansion + lazy abstract fetch
  const [expandedPmcid, setExpandedPmcid] = useState<string | null>(null)
  const [extrasCache, setExtrasCache] = useState<Map<string, ArticleExtras>>(new Map())
  const [extrasError, setExtrasError] = useState<string | null>(null)
  const [isLoadingExtras, setIsLoadingExtras] = useState(false)

  // Filter state
  const [cooccurrenceKws, setCooccurrenceKws] = useState<string[]>([])
  const [cooccurrenceLevel, setCooccurrenceLevel] = useState<'manuscript' | 'section' | 'sentence'>('manuscript')
  const [titleFilter, setTitleFilter] = useState('')
  const [sentenceFilter, setSentenceFilter] = useState('')
  const [sectionFilter, setSectionFilter] = useState('')
  const [filterYearRange, setFilterYearRange] = useState<[number, number]>([2000, 2026])
  // Per-term minimum occurrence thresholds: { searchTerm: minCount }
  const [keywordMinCounts, setKeywordMinCounts] = useState<Record<string, number>>({})
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)
  const filterAbortRef = useRef<AbortController | null>(null)

  // Helper: FastAPI 422 errors return detail as an array of validation errors.
  const fmtErr = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      return detail.map((d: any) => d.msg || String(d)).join('; ') || fallback
    }
    return fallback
  }

  // --- Workspace persistence ---
  const saveWorkspace = useCallback((overrides?: Partial<WorkspaceState>) => {
    const snapshot: WorkspaceState = {
      searchId,
      activeSessionId,
      lastDatasetId: sessionStorage.getItem('pmc_ev_last_dataset_id') ?? '',
      keywords,
      yearRange,
      selectedSections,
      articleTypes,
      entireManuscript,
      includeNonOA,
      searchedKeywords,
      isFiltered,
      cooccurrenceKws,
      cooccurrenceLevel,
      titleFilter,
      sentenceFilter,
      sectionFilter,
      filterYearRange,
      keywordMinCounts,
      resultsViewTab,
      ...overrides,
    }
    try {
      sessionStorage.setItem(WORKSPACE_KEY, JSON.stringify(snapshot))
      if (snapshot.lastDatasetId) {
        sessionStorage.setItem('pmc_ev_last_dataset_id', snapshot.lastDatasetId)
      } else {
        sessionStorage.removeItem('pmc_ev_last_dataset_id')
      }
      if (snapshot.searchId) {
        sessionStorage.setItem('pmc_ev_last_search_id', snapshot.searchId)
      }
    } catch {
      // ignore quota errors
    }
  }, [searchId, activeSessionId, keywords, yearRange, selectedSections, articleTypes, entireManuscript, includeNonOA, searchedKeywords, isFiltered, cooccurrenceKws, cooccurrenceLevel, titleFilter, sentenceFilter, sectionFilter, filterYearRange, keywordMinCounts, resultsViewTab])

  const restoreWorkspace = useCallback(() => {
    const raw = sessionStorage.getItem(WORKSPACE_KEY)
    if (!raw) return null
    try {
      return JSON.parse(raw) as WorkspaceState
    } catch {
      return null
    }
  }, [])

  useEffect(() => {
    loadSessions().then(() => {
      const ws = restoreWorkspace()
      if (ws) {
        // Restore session first so datasets load
        if (ws.activeSessionId) {
          setActiveSessionId(ws.activeSessionId)
        }
        // If a dataset was loaded, re-load it (this restores all params + results)
        if (ws.lastDatasetId) {
          loadDataset(ws.lastDatasetId)
        } else if (ws.searchId) {
          // Otherwise restore search params and fetch results
          setKeywords(ws.keywords)
          setYearRange(ws.yearRange)
          setSelectedSections(ws.selectedSections)
          setArticleTypes(ws.articleTypes)
          setEntireManuscript(ws.entireManuscript)
          setIncludeNonOA(ws.includeNonOA)
          setSearchedKeywords(ws.searchedKeywords)
          setIsFiltered(ws.isFiltered)
          setCooccurrenceKws(ws.cooccurrenceKws)
          setCooccurrenceLevel(ws.cooccurrenceLevel)
          setTitleFilter(ws.titleFilter)
          setSentenceFilter(ws.sentenceFilter)
          setSectionFilter(ws.sectionFilter)
          setFilterYearRange(ws.filterYearRange)
          setKeywordMinCounts(ws.keywordMinCounts || {})
          setResultsViewTab(ws.resultsViewTab)
          setSearchId(ws.searchId)
          sessionStorage.setItem('pmc_ev_last_search_id', ws.searchId)
          // Fetch results (use filtered endpoints if filters were active)
          setIsLoadingPage(true)
          const detailPromise = ws.isFiltered
            ? searchApi.getFilteredResults(ws.searchId, 1, pageSize)
            : searchApi.getResults(ws.searchId, 1, pageSize)
          const articlePromise = ws.isFiltered
            ? searchApi.getFilteredResultsByArticle(ws.searchId, 1, pageSize)
            : searchApi.getResultsByArticle(ws.searchId, 1, pageSize)
          Promise.all([detailPromise, articlePromise])
            .then(([detailRes, articleRes]) => {
              setResults(detailRes.data.results)
              setArticleResults(articleRes.data.results)
              setTotalCount(articleRes.data.count)
              setTotalPages(articleRes.data.total_pages)
              setPage(articleRes.data.page)
            })
            .catch(() => {
              sessionStorage.removeItem(WORKSPACE_KEY)
              sessionStorage.removeItem('pmc_ev_last_search_id')
              setSearchId('')
            })
            .finally(() => setIsLoadingPage(false))
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (activeSessionId) {
      loadSessionDatasets(activeSessionId)
    } else {
      setSessionDatasets([])
    }
  }, [activeSessionId])

  // Persist filtered flag so the top-nav Analytics link knows which cache to use
  useEffect(() => {
    sessionStorage.setItem('pmc_ev_last_search_filtered', String(isFiltered))
  }, [isFiltered])

  const loadSessions = refreshSessions

  const loadSessionDatasets = async (sid: string) => {
    try {
      const res = await datasetApi.list(sid)
      setSessionDatasets(res.data)
    } catch {
      setSessionDatasets([])
    }
  }

  const addKeyword = () => setKeywords([...keywords, { term: '', case_sensitive: false }])
  const removeKeyword = (idx: number) => setKeywords(keywords.filter((_, i) => i !== idx))
  const updateKeyword = (idx: number, field: keyof KeywordInput, value: any) => {
    const next = [...keywords]
    next[idx] = { ...next[idx], [field]: value }
    setKeywords(next)
  }

  const handleSearch = async () => {
    const validKeywords = keywords.filter(k => k.term.trim())
    if (validKeywords.length === 0) return
    if (!activeCorpusId) {
      alert('Select a database (top bar) before searching.')
      return
    }

    setIsSearching(true)
    setIsFiltered(false)
    setPage(1)
    // Start each new search with a clean filter slate — stale filters from a
    // previous search would otherwise carry over to different keywords.
    setCooccurrenceKws([])
    setTitleFilter('')
    setSentenceFilter('')
    setSectionFilter('')
    setFilterYearRange([2000, 2026])
    setKeywordMinCounts({})
    try {
      let restriction: string[] | undefined
      if (pmcidListMode === 'saved' && selectedRestrictionDataset) {
        // Restrict to the PMCIDs in the chosen dataset — only its PMCID column
        // is used. Synthetic PMID_ keys (abstract-only rows) aren't real PMCIDs
        // and never match the corpus, so we drop them.
        const ds = await datasetApi.get(selectedRestrictionDataset)
        const pmcids = (ds.data.data || [])
          .map((r: any) => (r?.pmcid ?? '').toString().trim())
          .filter((p: string) => p && !p.startsWith('PMID_'))
        restriction = Array.from(new Set(pmcids))
        if (restriction.length === 0) {
          alert('That dataset has no PMCIDs in its PMCID column to restrict by.')
          setIsSearching(false)
          return
        }
      } else if (pmcidListMode === 'file' && uploadedFile) {
        const text = await uploadedFile.text()
        restriction = text.split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
      }

      const res = await searchApi.createSearch({
        corpus_id: activeCorpusId,
        keywords: validKeywords,
        year_range: yearRange,
        sections: entireManuscript ? undefined : selectedSections,
        article_types: articleTypes,
        entire_manuscript: entireManuscript,
        include_non_oa: includeNonOA,
        pmcid_restriction: restriction,
      })

      const sid = res.data.search.search_id
      setResults(res.data.results)
      setSearchId(sid)
      setTotalCount(res.data.count)
      setTotalPages(Math.max(1, Math.ceil(res.data.count / pageSize)))
      setSearchedKeywords(validKeywords.map(k => k.term))
      setResultsViewTab('by-article')
      setArticleResults([])
      setActiveTab('filter')
      // Persist search ID so it survives navigation to other pages
      sessionStorage.setItem('pmc_ev_last_search_id', sid)
      // Also fetch by-article data so the count reflects unique articles
      const articleRes = await searchApi.getResultsByArticle(sid, 1, pageSize)
      setArticleResults(articleRes.data.results)
      setTotalCount(articleRes.data.count)
      setTotalPages(Math.max(1, Math.ceil(articleRes.data.count / pageSize)))
      setTimeout(() => saveWorkspace({ searchId: sid, lastDatasetId: '', isFiltered: false, searchedKeywords: validKeywords.map(k => k.term) }), 0)
    } catch (e: any) {
      alert(fmtErr(e, 'Search failed'))
    } finally {
      setIsSearching(false)
    }
  }

  const handleBuildRegex = async () => {
    if (!regexDescription.trim()) return
    setIsBuildingRegex(true)
    setRegexResult(null)
    try {
      const res = await aiApi.buildRegex({ description: regexDescription.trim() })
      setRegexResult(res.data)
    } catch (e: any) {
      alert(fmtErr(e, 'Failed to build regex'))
    } finally {
      setIsBuildingRegex(false)
    }
  }

  const addRegexAsKeyword = (pattern: string) => {
    setKeywords([...keywords, { term: pattern, case_sensitive: false }])
    setShowRegexBuilder(false)
    setRegexDescription('')
    setRegexResult(null)
  }

  const fetchPage = useCallback(async (targetPage: number, targetPageSize?: number, targetView?: 'by-article' | 'detailed', targetFiltered?: boolean, targetSortBy?: ByArticleSortKey, targetSortDir?: SortDir) => {
    if (!searchId) return
    const ps = targetPageSize || pageSize
    const view = targetView || resultsViewTab
    const filtered = targetFiltered !== undefined ? targetFiltered : isFiltered
    const sb = targetSortBy ?? sortBy
    const sd = targetSortDir ?? sortDir
    setIsLoadingPage(true)
    try {
      if (view === 'by-article') {
        const res = filtered
          ? await searchApi.getFilteredResultsByArticle(searchId, targetPage, ps, sb, sd)
          : await searchApi.getResultsByArticle(searchId, targetPage, ps, sb, sd)
        setArticleResults(res.data.results)
        setTotalCount(res.data.count)
        setTotalPages(res.data.total_pages)
        setPage(res.data.page)
      } else {
        const res = filtered
          ? await searchApi.getFilteredResults(searchId, targetPage, ps)
          : await searchApi.getResults(searchId, targetPage, ps)
        setResults(res.data.results)
        setTotalCount(res.data.count)
        setTotalPages(res.data.total_pages)
        setPage(res.data.page)
      }
    } catch (e: any) {
      alert(fmtErr(e, 'Failed to load page'))
    } finally {
      setIsLoadingPage(false)
    }
  }, [searchId, isFiltered, pageSize, resultsViewTab, sortBy, sortDir])

  const applyServerFilters = async () => {
    if (!searchId) return
    // Cancel any in-flight filter request so a slower previous response
    // can't overwrite the cache and stomp on the newer one.
    filterAbortRef.current?.abort()
    const controller = new AbortController()
    filterAbortRef.current = controller
    setIsApplyingFilters(true)
    setPage(1)
    try {
      const filterData: PostSearchFilter = {
        cooccurrence: cooccurrenceKws.length >= 2
          ? { keywords: cooccurrenceKws, level: cooccurrenceLevel }
          : undefined,
        free_text_filters: {
          ...(titleFilter ? { title_contains: titleFilter } : {}),
          ...(sentenceFilter ? { sentence_contains: sentenceFilter } : {}),
          ...(sectionFilter ? { section_contains: sectionFilter } : {}),
        },
        year_range: filterYearRange,
        keyword_min_counts: Object.keys(keywordMinCounts).length > 0 ? keywordMinCounts : undefined,
      }
      await searchApi.filterResults(searchId, filterData, controller.signal)
      if (controller.signal.aborted) return
      setIsFiltered(true)
      // Fetch first page with the new filtered state
      await fetchPage(1, undefined, resultsViewTab, true)
      if (controller.signal.aborted) return
      setTimeout(() => saveWorkspace({ isFiltered: true, cooccurrenceKws, cooccurrenceLevel, titleFilter, sentenceFilter, sectionFilter, filterYearRange, keywordMinCounts }), 0)
    } catch (e: any) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || controller.signal.aborted) return
      alert(fmtErr(e, 'Filter failed'))
    } finally {
      if (filterAbortRef.current === controller) {
        filterAbortRef.current = null
        setIsApplyingFilters(false)
      }
    }
  }

  const handleByArticleSort = useCallback((col: ByArticleSortKey) => {
    // Toggle direction if same column, otherwise default to desc.
    const nextDir: SortDir = sortBy === col && sortDir === 'desc' ? 'asc' : (sortBy === col ? 'desc' : 'desc')
    setSortBy(col)
    setSortDir(nextDir)
    setExpandedPmcid(null)
    if (searchId && articleResults.length > 0) {
      // Backend mode — refetch first page with new sort
      fetchPage(1, undefined, 'by-article', undefined, col, nextDir)
    }
  }, [sortBy, sortDir, searchId, articleResults.length, fetchPage])

  const toggleExpandRow = useCallback(async (pmcid: string) => {
    if (expandedPmcid === pmcid) {
      setExpandedPmcid(null)
      setExtrasError(null)
      return
    }
    setExpandedPmcid(pmcid)
    setExtrasError(null)
    if (extrasCache.has(pmcid)) return
    setIsLoadingExtras(true)
    try {
      const res = await articleApi.getExtras(pmcid, activeCorpusId)
      setExtrasCache(prev => {
        const next = new Map(prev)
        next.set(pmcid, res.data)
        return next
      })
    } catch (e: any) {
      const detail = fmtErr(e, 'Failed to load article details')
      setExtrasError(detail)
    } finally {
      setIsLoadingExtras(false)
    }
  }, [expandedPmcid, extrasCache])

  const clearFilters = async () => {
    setIsFiltered(false)
    setPage(1)
    setCooccurrenceKws([])
    setTitleFilter('')
    setSentenceFilter('')
    setSectionFilter('')
    setFilterYearRange([2000, 2026])
    setKeywordMinCounts({})
    if (searchId) {
      await fetchPage(1, undefined, resultsViewTab, false)
    }
    setTimeout(() => saveWorkspace({ isFiltered: false, cooccurrenceKws: [], titleFilter: '', sentenceFilter: '', sectionFilter: '', filterYearRange: [2000, 2026], keywordMinCounts: {} }), 0)
  }

  // Build co-occurrence keyword options from searched keywords
  // (NOT from paginated results, which may only contain a subset)
  const uniqueKeywordOptions = searchedKeywords.length > 0
    ? searchedKeywords
    : Array.from(new Set(results.map(r => r.keyword).filter(Boolean)))

  // Fetch the COMPLETE set of detailed (sentence-level) rows for the current
  // search/filter. Drives pagination off the server-reported detailed count/pages
  // from the first response — NOT the view-dependent `totalCount` state, which on
  // the by-article tab holds the article count and would truncate the result set.
  const fetchAllDetailedResults = async (): Promise<SearchResultRow[]> => {
    const all: SearchResultRow[] = []
    const ps = 500
    const first = isFiltered
      ? await searchApi.getFilteredResults(searchId, 1, ps)
      : await searchApi.getResults(searchId, 1, ps)
    all.push(...first.data.results)
    const pages = first.data.total_pages ?? Math.ceil((first.data.count ?? all.length) / ps)
    for (let p = 2; p <= pages; p++) {
      const res = isFiltered
        ? await searchApi.getFilteredResults(searchId, p, ps)
        : await searchApi.getResults(searchId, p, ps)
      all.push(...res.data.results)
    }
    return all
  }

  const saveDataset = async () => {
    if (!activeSessionId) {
      alert('Please select or create a session to save datasets.')
      return
    }
    const name = prompt('Enter a name for this dataset:')
    if (!name) return
    try {
      const allResults = await fetchAllDetailedResults()
      await datasetApi.create({
        name,
        session_id: activeSessionId,
        search_id: searchId || undefined,
        data: allResults as any,
        metadata: {
          query_params: {
            keywords: keywords.filter(k => k.term.trim()).map(k => ({ term: k.term, case_sensitive: k.case_sensitive })),
            year_range: yearRange,
            sections: selectedSections,
            article_types: articleTypes,
            entire_manuscript: entireManuscript,
            include_non_oa: includeNonOA,
          },
          filter_state: isFiltered ? {
            cooccurrence_kws: cooccurrenceKws,
            cooccurrence_level: cooccurrenceLevel,
            title_filter: titleFilter,
            sentence_filter: sentenceFilter,
            section_filter: sectionFilter,
            filter_year_range: filterYearRange,
            keyword_min_counts: keywordMinCounts,
          } : null,
          is_filtered: isFiltered,
          searched_keywords: searchedKeywords,
        },
      })
      alert('Dataset saved!')
      await loadSessionDatasets(activeSessionId)
    } catch (e: any) {
      alert(fmtErr(e, 'Failed to save dataset'))
    }
  }

  const loadDataset = async (datasetId: string) => {
    try {
      const res = await datasetApi.get(datasetId)
      setResults(res.data.data as SearchResultRow[])
      setTotalCount(res.data.row_count)
      setTotalPages(1)
      setPage(1)
      setResultsViewTab('by-article')
      setArticleResults([])

      // Restore search/filter state from metadata
      const meta = res.data.metadata
      if (meta?.query_params) {
        const qp = meta.query_params
        if (qp.keywords) setKeywords(qp.keywords)
        if (qp.year_range) setYearRange(qp.year_range)
        if (qp.sections) setSelectedSections(qp.sections)
        if (qp.article_types) setArticleTypes(qp.article_types)
        if (qp.entire_manuscript !== undefined) setEntireManuscript(qp.entire_manuscript)
        if (qp.include_non_oa !== undefined) setIncludeNonOA(qp.include_non_oa)
        setSearchedKeywords((qp.keywords || []).map((k: any) => k.term))
      }
      if (meta?.filter_state) {
        const fs = meta.filter_state
        setCooccurrenceKws(fs.cooccurrence_kws || [])
        setCooccurrenceLevel(fs.cooccurrence_level || 'manuscript')
        setTitleFilter(fs.title_filter || '')
        setSentenceFilter(fs.sentence_filter || '')
        setSectionFilter(fs.section_filter || '')
        setFilterYearRange(fs.filter_year_range || [2000, 2026])
        setKeywordMinCounts(fs.keyword_min_counts || {})
      }
      setIsFiltered(meta?.is_filtered || false)

      if (res.data.session_id) {
        setActiveSessionId(res.data.session_id)
      }
      if (res.data.search_id) {
        setSearchId(res.data.search_id)
        sessionStorage.setItem('pmc_ev_last_search_id', res.data.search_id)
      }
      const loadedIsFiltered = meta?.is_filtered || false
      const loadedSearchId = res.data.search_id || searchId
      setTimeout(() => saveWorkspace({
        searchId: loadedSearchId,
        lastDatasetId: datasetId,
        activeSessionId: res.data.session_id || activeSessionId,
        isFiltered: loadedIsFiltered,
        cooccurrenceKws: meta?.filter_state?.cooccurrence_kws || [],
        cooccurrenceLevel: meta?.filter_state?.cooccurrence_level || 'manuscript',
        titleFilter: meta?.filter_state?.title_filter || '',
        sentenceFilter: meta?.filter_state?.sentence_filter || '',
        sectionFilter: meta?.filter_state?.section_filter || '',
        filterYearRange: meta?.filter_state?.filter_year_range || [2000, 2026],
        keywordMinCounts: meta?.filter_state?.keyword_min_counts || {},
      }), 0)
    } catch (e: any) {
      alert(fmtErr(e, 'Failed to load dataset'))
    }
  }

  const deleteDataset = async (datasetId: string) => {
    if (!confirm('Delete this dataset?')) return
    try {
      await datasetApi.delete(datasetId)
      if (activeSessionId) await loadSessionDatasets(activeSessionId)
    } catch (e: any) {
      alert(fmtErr(e, 'Failed to delete dataset'))
    }
  }

  const exportCSV = async () => {
    try {
      const allResults = await fetchAllDetailedResults()
      if (allResults.length === 0) return
      // Clean the synthetic `PMID_` key out of the exported identifier and add a
      // canonical NCBI/PMC url column, so IDs in the CSV resolve correctly.
      const cleaned = allResults.map(row => {
        const isPmidOnly = row.pmcid?.startsWith('PMID_')
        return {
          ...row,
          pmcid: isPmidOnly ? '' : row.pmcid,
          pmid: isPmidOnly ? (row.pmid ?? row.pmcid.replace('PMID_', '')) : (row.pmid ?? ''),
          url: articleUrl(row),
        }
      })
      const headers = Object.keys(cleaned[0])
      const csv = [
        headers.join(','),
        ...cleaned.map(row => headers.map(h => JSON.stringify((row as any)[h] ?? '')).join(','))
      ].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ev_search_results_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
    } catch (e: any) {
      alert(fmtErr(e, 'Export failed'))
    }
  }

  // uniqueKeywordOptions is defined above clearFilters now

  // Aggregate results by PMCID for the "by-article" view
  const aggregatedByArticle = (() => {
    const map = new Map<string, {
      pmcid: string
      title?: string
      year?: number
      oa_status?: string
      keywords: Set<string>
      matchedValues: Map<string, number>  // extracted value -> total occurrences
      matchCount: number
    }>()
    for (const row of results) {
      const existing = map.get(row.pmcid)
      if (existing) {
        if (row.keyword) existing.keywords.add(row.keyword)
        if (row.matched_values) {
          const prev = existing.matchedValues.get(row.matched_values) || 0
          existing.matchedValues.set(row.matched_values, prev + 1)
        }
        existing.matchCount += 1
      } else {
        const mvMap = new Map<string, number>()
        if (row.matched_values) {
          mvMap.set(row.matched_values, 1)
        }
        map.set(row.pmcid, {
          pmcid: row.pmcid,
          title: row.title,
          year: row.year,
          oa_status: row.oa_status,
          keywords: new Set(row.keyword ? [row.keyword] : []),
          matchedValues: mvMap,
          matchCount: 1,
        })
      }
    }
    return Array.from(map.values()).sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
  })()

  const PaginationControls = () => (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <div className="flex items-center gap-2">
        <button
          onClick={() => fetchPage(page - 1)}
          disabled={page <= 1 || isLoadingPage}
          className="px-3 py-1 border rounded text-sm hover:bg-gray-50 disabled:opacity-40"
        >
          ← Prev
        </button>
        <span className="text-sm text-gray-600">
          Page <strong>{page}</strong> of <strong>{totalPages}</strong>
        </span>
        <button
          onClick={() => fetchPage(page + 1)}
          disabled={page >= totalPages || isLoadingPage}
          className="px-3 py-1 border rounded text-sm hover:bg-gray-50 disabled:opacity-40"
        >
          Next →
        </button>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">{totalCount.toLocaleString()} total</span>
        <select
          value={pageSize}
          onChange={e => {
            const ps = Number(e.target.value)
            setPageSize(ps)
            fetchPage(1, ps)
          }}
          className="text-sm border rounded px-2 py-1"
        >
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
          <option value={200}>200 / page</option>
        </select>
      </div>
    </div>
  )

  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      <div className="w-80 shrink-0">
        {/* Dataset Manager — always visible */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Datasets</label>

          <div className="flex gap-2 mb-3">
            <button
              onClick={saveDataset}
              disabled={!activeSessionId}
              title={activeSessionId ? undefined : 'Select or create a session first'}
              className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Dataset
            </button>
            <button onClick={exportCSV} className="flex-1 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700">
              Export CSV
            </button>
          </div>
          {!activeSessionId && (
            <p className="text-xs text-gray-500 mb-3">Select or create a session above to save datasets.</p>
          )}

          {/* Session Datasets */}
          {activeSessionId && sessionDatasets.length > 0 && (
            <div className="space-y-2">
              {sessionDatasets.map(ds => (
                <div key={ds.dataset_id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{ds.name}</div>
                    <div className="text-xs text-gray-500">{ds.row_count.toLocaleString()} rows</div>
                  </div>
                  <div className="flex gap-2 ml-2 shrink-0">
                    <button
                      onClick={() => loadDataset(ds.dataset_id)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => deleteDataset(ds.dataset_id)}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Del
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeSessionId && sessionDatasets.length === 0 && (
            <div className="text-xs text-gray-400">
              No datasets in this session yet.
            </div>
          )}

          {!activeSessionId && (
            <div className="text-xs text-yellow-600 bg-yellow-50 rounded px-2 py-1">
              Select or create a session to save datasets.
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveTab('search')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg ${activeTab === 'search' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Search
            </button>
            <button
              onClick={() => setActiveTab('filter')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg ${activeTab === 'filter' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Filter
            </button>
          </div>

          {activeTab === 'search' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Keywords / Regex</label>
                {keywords.map((kw, idx) => (
                  <div key={idx} className="flex gap-2 mb-2 items-center">
                    <input
                      type="text"
                      value={kw.term}
                      onChange={e => updateKeyword(idx, 'term', e.target.value)}
                      placeholder="e.g. exosome|microvesicle"
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    />
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={kw.case_sensitive}
                        onChange={e => updateKeyword(idx, 'case_sensitive', e.target.checked)}
                      />
                      Aa
                    </label>
                    {keywords.length > 1 && (
                      <button onClick={() => removeKeyword(idx)} className="text-red-500 text-sm">×</button>
                    )}
                  </div>
                ))}
                <button onClick={addKeyword} className="text-sm text-blue-600 hover:underline">+ Add keyword</button>
                <button
                  onClick={() => setShowRegexBuilder(true)}
                  className="text-sm text-purple-600 hover:underline ml-3"
                  title="Let AI generate a regex pattern from your description"
                >
                  ✨ AI Regex Builder
                </button>
                <button
                  onClick={() => setShowCheatsheet(true)}
                  className="text-sm text-blue-600 hover:underline ml-3"
                  title="Regex syntax reference with EV-research examples"
                >
                  📖 Regex Cheatsheet
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year Range</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    value={yearRange[0]}
                    onChange={e => setYearRange([Number(e.target.value), yearRange[1]])}
                    className="w-20 px-2 py-1 border rounded text-sm"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    value={yearRange[1]}
                    onChange={e => setYearRange([yearRange[0], Number(e.target.value)])}
                    className="w-20 px-2 py-1 border rounded text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={entireManuscript} onChange={e => setEntireManuscript(e.target.checked)} />
                  Search entire manuscript
                </label>
              </div>

              {!entireManuscript && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sections</label>
                  <div className="flex flex-wrap gap-2">
                    {SECTION_OPTIONS.map(s => (
                      <label key={s} className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedSections.includes(s)}
                          onChange={e => {
                            if (e.target.checked) setSelectedSections([...selectedSections, s])
                            else setSelectedSections(selectedSections.filter(x => x !== s))
                          }}
                        />
                        {s}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Article Types</label>
                <div className="flex gap-4">
                  {ARTICLE_TYPE_OPTIONS.map(t => (
                    <label key={t} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={articleTypes.includes(t)}
                        onChange={e => {
                          if (e.target.checked) setArticleTypes([...articleTypes, t])
                          else setArticleTypes(articleTypes.filter(x => x !== t))
                        }}
                      />
                      {t}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={includeNonOA} onChange={e => setIncludeNonOA(e.target.checked)} />
                  Include non-OA articles
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PMCID Restriction</label>
                <select
                  value={pmcidListMode}
                  onChange={e => setPmcidListMode(e.target.value as any)}
                  className="w-full px-2 py-1 border rounded text-sm mb-2"
                >
                  <option value="none">None</option>
                  <option value="file">Upload file</option>
                  <option value="saved">Saved dataset</option>
                </select>
                {pmcidListMode === 'file' && (
                  <input type="file" accept=".csv,.txt" onChange={e => setUploadedFile(e.target.files?.[0] || null)} className="text-sm" />
                )}
                {pmcidListMode === 'saved' && (
                  !activeSessionId ? (
                    <p className="text-xs text-gray-500">Select a session to choose a dataset.</p>
                  ) : sessionDatasets.length === 0 ? (
                    <p className="text-xs text-gray-500">No saved datasets in this session yet.</p>
                  ) : (
                    <>
                      <select
                        value={selectedRestrictionDataset}
                        onChange={e => setSelectedRestrictionDataset(e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">Select dataset...</option>
                        {sessionDatasets.map(d => (
                          <option key={d.dataset_id} value={d.dataset_id}>{d.name} ({d.row_count} rows)</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">Only the dataset's PMCID column is used.</p>
                    </>
                  )
                )}
              </div>

              <button
                onClick={handleSearch}
                disabled={isSearching || !activeCorpusId}
                title={activeCorpusId ? undefined : 'Select a database in the top bar first'}
                className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSearching && <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                {isSearching ? 'Searching…' : 'Search'}
              </button>
              {!activeCorpusId && (
                <p className="text-xs text-amber-600 text-center">Select a database (top bar) to search.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Co-occurrence Keywords</label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {uniqueKeywordOptions.map(kw => (
                    <label key={kw} className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={cooccurrenceKws.includes(kw!)}
                        onChange={e => {
                          if (e.target.checked) setCooccurrenceKws([...cooccurrenceKws, kw!])
                          else setCooccurrenceKws(cooccurrenceKws.filter(x => x !== kw))
                        }}
                      />
                      {kw}
                    </label>
                  ))}
                </div>
                <select
                  value={cooccurrenceLevel}
                  onChange={e => setCooccurrenceLevel(e.target.value as any)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="manuscript">Same manuscript</option>
                  <option value="section">Same section</option>
                  <option value="sentence">Same sentence</option>
                </select>
              </div>

              {uniqueKeywordOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min. occurrences per term</label>
                  <p className="text-xs text-gray-500 mb-2">
                    Keep only articles where each search/regex term appears at least N times (minimum 1). Counts the term itself, not regex-extracted values.
                  </p>
                  <div className="space-y-1">
                    {uniqueKeywordOptions.map(kw => (
                      <div key={kw} className="flex items-center gap-2">
                        <span className="text-xs flex-1 break-words" title={kw!}>{kw}</span>
                        <input
                          type="number"
                          min={1}
                          value={keywordMinCounts[kw!] ?? 1}
                          onChange={e => {
                            const n = Math.max(1, Math.floor(Number(e.target.value) || 1))
                            setKeywordMinCounts(prev => ({ ...prev, [kw!]: n }))
                          }}
                          className="w-16 px-2 py-1 border rounded text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title contains</label>
                <input
                  type="text"
                  value={titleFilter}
                  onChange={e => setTitleFilter(e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sentence contains</label>
                <input
                  type="text"
                  value={sentenceFilter}
                  onChange={e => setSentenceFilter(e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section contains</label>
                <input
                  type="text"
                  value={sectionFilter}
                  onChange={e => setSectionFilter(e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter Year Range</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    value={filterYearRange[0]}
                    onChange={e => setFilterYearRange([Number(e.target.value), filterYearRange[1]])}
                    className="w-20 px-2 py-1 border rounded text-sm"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    value={filterYearRange[1]}
                    onChange={e => setFilterYearRange([filterYearRange[0], Number(e.target.value)])}
                    className="w-20 px-2 py-1 border rounded text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={applyServerFilters}
                  disabled={isApplyingFilters || !searchId}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {isApplyingFilters ? 'Applying...' : 'Apply Filters'}
                </button>
                <button
                  onClick={clearFilters}
                  disabled={!isFiltered || !searchId}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
                >
                  Clear
                </button>
              </div>

              {isFiltered && (
                <div className="text-xs text-blue-600 bg-blue-50 rounded px-2 py-1">
                  Filters active — {totalCount.toLocaleString()} matches
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {isSearching && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent shrink-0" />
              <div>
                <div className="font-medium text-slate-800">
                  Searching {activeCorpus?.name ? <span className="font-semibold">{activeCorpus.name}</span> : 'the corpus'}…
                </div>
                <div className="text-sm text-gray-500">
                  Scanning full text across {activeCorpus?.article_count ? `${activeCorpus.article_count.toLocaleString()} manuscripts` : 'the database'}. Broad queries or regex can take a little longer — hang tight.
                </div>
              </div>
            </div>
            <div className="indeterminate-track h-1.5 w-full bg-gray-100 rounded-full">
              <div className="indeterminate-fill bg-blue-500" />
            </div>
          </div>
        )}
        {searchId && !isSearching && (
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                {resultsViewTab === 'by-article' ? (
                  <>
                    <span className="font-medium">{totalCount.toLocaleString()}</span>
                    <span className="text-gray-500 text-sm ml-1">unique articles</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium">{totalCount.toLocaleString()}</span>
                    <span className="text-gray-500 text-sm ml-1">matches</span>
                  </>
                )}
                {isFiltered && (
                  <span className="text-blue-600 text-sm ml-2 font-medium">(filtered)</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => {
                      if (resultsViewTab !== 'by-article') {
                        setResultsViewTab('by-article')
                        setPage(1)
                        if (searchId) fetchPage(1, undefined, 'by-article')
                      }
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-md ${resultsViewTab === 'by-article' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    By Article
                  </button>
                  <button
                    onClick={() => {
                      if (resultsViewTab !== 'detailed') {
                        setResultsViewTab('detailed')
                        setPage(1)
                        if (searchId) fetchPage(1, undefined, 'detailed')
                      }
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-md ${resultsViewTab === 'detailed' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Detailed
                  </button>
                </div>
                {resultsViewTab === 'detailed' && (
                  <div className="text-sm text-gray-500">
                    {new Set(results.map(r => r.pmcid)).size.toLocaleString()} unique articles on this page
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              {resultsViewTab === 'detailed' ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">PMCID</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Title</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Matched Text</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Section</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Keyword</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Year</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">OA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {isLoadingPage ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                          Loading...
                        </td>
                      </tr>
                    ) : results.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                          No results found.
                        </td>
                      </tr>
                    ) : (
                      results.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 align-top">
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                              <BookmarkStar pmcid={row.pmcid} title={row.title} />
                              <a
                                href={articleUrl(row)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline font-mono"
                              >
                                {articleLabel(row)}
                              </a>
                            </div>
                          </td>
                          <td className="px-4 py-2"><div className="max-w-sm break-words whitespace-normal">{row.title}</div></td>
                          <td className="px-4 py-2"><div className="max-w-md break-words whitespace-normal">{row.text}</div></td>
                          <td className="px-4 py-2">{row.section_standard}</td>
                          <td className="px-4 py-2">
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">{row.keyword}</span>
                          </td>
                          <td className="px-4 py-2">{row.year}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${row.oa_status === 'OA' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                              {row.oa_status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortHeader col="pmcid" label="PMCID" sortBy={sortBy} sortDir={sortDir} onSort={handleByArticleSort} />
                      <SortHeader col="title" label="Title" sortBy={sortBy} sortDir={sortDir} onSort={handleByArticleSort} />
                      <SortHeader col="year" label="Year" sortBy={sortBy} sortDir={sortDir} onSort={handleByArticleSort} />
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Extracted Keywords</th>
                      <SortHeader col="match_count" label="Matches" sortBy={sortBy} sortDir={sortDir} onSort={handleByArticleSort} />
                      <th className="px-4 py-2 text-left font-medium text-gray-700">OA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {isLoadingPage ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                          Loading...
                        </td>
                      </tr>
                    ) : (
                      (() => {
                        // Use backend-aggregated results when available (search mode),
                        // otherwise fall back to client-side aggregation (dataset mode).
                        // In search mode, the backend already applied sortBy/sortDir.
                        // In dataset mode, we sort client-side here.
                        let articles = articleResults.length > 0
                          ? articleResults.map(ar => ({
                              pmcid: ar.pmcid,
                              title: ar.title,
                              year: ar.year,
                              oa_status: ar.oa_status,
                              keywords: ar.keywords,
                              matchedValues: Object.entries(ar.matched_values).sort((a, b) => b[1] - a[1]),
                              matchCount: ar.match_count,
                            }))
                          : aggregatedByArticle.map(ar => ({
                              pmcid: ar.pmcid,
                              title: ar.title,
                              year: ar.year,
                              oa_status: ar.oa_status,
                              keywords: Array.from(ar.keywords),
                              matchedValues: Array.from(ar.matchedValues.entries()).sort((a, b) => b[1] - a[1]),
                              matchCount: ar.matchCount,
                            }))
                        if (articleResults.length === 0 && articles.length > 0) {
                          // Dataset mode — sort client-side
                          const keyFn = (a: typeof articles[number]): string | number => {
                            switch (sortBy) {
                              case 'pmcid': return (a.pmcid || '').toLowerCase()
                              case 'title': return (a.title || '').toLowerCase()
                              case 'year': return a.year || 0
                              case 'match_count': return a.matchCount || 0
                            }
                          }
                          articles = [...articles].sort((a, b) => {
                            const ka = keyFn(a), kb = keyFn(b)
                            if (ka < kb) return sortDir === 'asc' ? -1 : 1
                            if (ka > kb) return sortDir === 'asc' ? 1 : -1
                            return 0
                          })
                        }
                        if (articles.length === 0) {
                          return (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                                No results found.
                              </td>
                            </tr>
                          )
                        }
                        return articles.flatMap(article => {
                          const isExpanded = expandedPmcid === article.pmcid
                          const extras = extrasCache.get(article.pmcid)
                          const rows = [
                            <tr
                              key={article.pmcid}
                              className={`hover:bg-gray-50 cursor-pointer align-top ${isExpanded ? 'bg-blue-50' : ''}`}
                              onClick={() => toggleExpandRow(article.pmcid)}
                            >
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-1.5 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                                  <BookmarkStar pmcid={article.pmcid} title={article.title} />
                                  <a
                                    href={articleUrl(article)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline font-mono"
                                  >
                                    {articleLabel(article)}
                                  </a>
                                </div>
                              </td>
                              <td className="px-4 py-2"><div className="max-w-sm break-words whitespace-normal">{article.title}</div></td>
                              <td className="px-4 py-2 whitespace-nowrap">{article.year}</td>
                              <td className="px-4 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {article.matchedValues.length > 0 ? (
                                    article.matchedValues.map(([value, count]) => (
                                      <span key={value} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                                        {value} ({count})
                                      </span>
                                    ))
                                  ) : (
                                    article.keywords.map(kw => (
                                      <span key={kw} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">{kw}</span>
                                    ))
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-gray-600">{article.matchCount}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${article.oa_status === 'OA' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                  {article.oa_status}
                                </span>
                              </td>
                            </tr>
                          ]
                          if (isExpanded) {
                            rows.push(
                              <tr key={`${article.pmcid}-extras`} className="bg-blue-50">
                                <td colSpan={6} className="px-6 py-4">
                                  {isLoadingExtras && !extras ? (
                                    <div className="text-gray-500 text-xs">Loading abstract…</div>
                                  ) : extrasError && !extras ? (
                                    <div className="text-red-600 text-xs">{extrasError}</div>
                                  ) : extras ? (
                                    <div className="space-y-2 text-xs text-gray-800">
                                      {extras.authors && (
                                        <div><span className="font-semibold">Authors:</span> {extras.authors}</div>
                                      )}
                                      {extras.journal && (
                                        <div><span className="font-semibold">Journal:</span> {extras.journal}</div>
                                      )}
                                      <div>
                                        <div className="font-semibold mb-1">Abstract:</div>
                                        <div className="max-h-64 overflow-y-auto bg-white p-3 rounded border whitespace-pre-line">
                                          {extras.abstract || <span className="text-gray-400 italic">No abstract available.</span>}
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}
                                </td>
                              </tr>
                            )
                          }
                          return rows
                        })
                      })()
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <PaginationControls />
          </div>
        )}
      </div>

      {/* Regex Cheatsheet Modal */}
      {showCheatsheet && <RegexCheatsheet onClose={() => setShowCheatsheet(false)} />}

      {/* AI Regex Builder Modal */}
      {showRegexBuilder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">✨ AI Regex Builder</h2>
                <button
                  onClick={() => {
                    setShowRegexBuilder(false)
                    setRegexResult(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-3">
                Describe what you want to search for in plain English, and AI will generate a regex pattern.
              </p>

              <textarea
                value={regexDescription}
                onChange={e => setRegexDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg text-sm mb-3"
                placeholder="e.g., exosomes or microvesicles or extracellular vesicles"
              />

              <div className="flex gap-2 mb-4">
                <button
                  onClick={handleBuildRegex}
                  disabled={isBuildingRegex || !regexDescription.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                >
                  {isBuildingRegex ? 'Building...' : 'Generate Regex'}
                </button>
              </div>

              {regexResult && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs text-gray-500 uppercase font-medium mb-1">Pattern</div>
                    <code className="block bg-gray-900 text-green-400 px-3 py-2 rounded text-sm font-mono break-all">
                      {regexResult.pattern}
                    </code>
                    <button
                      onClick={() => addRegexAsKeyword(regexResult.pattern)}
                      className="mt-2 text-sm text-purple-600 hover:underline font-medium"
                    >
                      + Add as keyword
                    </button>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 uppercase font-medium mb-1">Explanation</div>
                    <p className="text-sm text-gray-700">{regexResult.explanation}</p>
                  </div>

                  {regexResult.suggestions.length > 0 && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase font-medium mb-2">Suggestions</div>
                      <div className="space-y-2">
                        {regexResult.suggestions.map((s, i) => (
                          <div key={i} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                            <code className="text-sm font-mono text-gray-800 break-all">{s}</code>
                            <button
                              onClick={() => addRegexAsKeyword(s)}
                              className="text-xs text-purple-600 hover:underline ml-2 shrink-0"
                            >
                              Add
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
